import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, CircleNotch, EnvelopeSimple } from "@phosphor-icons/react";
import axios from "axios";
import { API } from "@/App";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#262626] last:border-0">
      <div><p className="text-sm text-white">{label}</p><p className="text-xs text-[#666]">{description}</p></div>
      <button onClick={() => onChange(!checked)} className={`w-10 h-5 rounded-full transition-colors ${checked ? "bg-[#ccff00]" : "bg-[#333]"} relative`}>
        <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const unsubAll = params.get("all") === "true";

  const [prefs, setPrefs] = useState({ weekly_buy_sell: true, weekly_rookie: true, weekly_trade: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [active, setActive] = useState(true);
  const [found, setFound] = useState(false);

  useEffect(() => {
    if (!email) { setLoading(false); return; }
    axios.get(`${API}/unsubscribe?email=${encodeURIComponent(email)}${unsubAll ? "&all=true" : ""}`)
      .then(r => {
        setFound(r.data.found);
        setActive(r.data.active);
        if (r.data.prefs) setPrefs(r.data.prefs);
        if (unsubAll && r.data.found) {
          // Auto-unsubscribe all
          axios.put(`${API}/unsubscribe`, { email, unsubscribe_all: true }).then(() => { setActive(false); setSaved(true); });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [email, unsubAll]);

  const save = async (newPrefs) => {
    setPrefs(newPrefs);
    setSaving(true);
    try {
      await axios.put(`${API}/unsubscribe`, { email, prefs: newPrefs });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  };

  const unsubscribeAll = async () => {
    setSaving(true);
    await axios.put(`${API}/unsubscribe`, { email, unsubscribe_all: true });
    setActive(false);
    setSaved(true);
    setSaving(false);
  };

  const resubscribe = async () => {
    setSaving(true);
    await axios.put(`${API}/unsubscribe`, { email, prefs: { weekly_buy_sell: true, weekly_rookie: true, weekly_trade: true } });
    setActive(true);
    setPrefs({ weekly_buy_sell: true, weekly_rookie: true, weekly_trade: true });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Email Preferences — Dynasty Trade Maker" path="/unsubscribe" />
      <PublicNav />
      <div className="max-w-lg mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <EnvelopeSimple size={32} className="text-[#ccff00] mx-auto mb-3" weight="bold" />
          <h1 className="font-heading text-2xl text-white uppercase mb-2">EMAIL PREFERENCES</h1>
          {email && <p className="text-sm text-[#a3a3a3]">{email}</p>}
        </div>

        {!email ? (
          <div className="text-center py-12 border border-[#262626] bg-[#171717] rounded-sm">
            <p className="text-[#a3a3a3] text-sm">{"No email address provided. Use the link from your email to manage preferences."}</p>
          </div>
        ) : loading ? (
          <div className="text-center py-12"><CircleNotch size={24} className="animate-spin text-[#ccff00] mx-auto" /></div>
        ) : !found ? (
          <div className="text-center py-12 border border-[#262626] bg-[#171717] rounded-sm">
            <p className="text-[#a3a3a3] text-sm">{"This email wasn't found in our subscriber list."}</p>
          </div>
        ) : !active ? (
          <div className="text-center py-12 border border-[#262626] bg-[#171717] rounded-sm">
            <Check size={32} className="text-[#ccff00] mx-auto mb-3" weight="bold" />
            <h2 className="font-heading text-xl text-white uppercase mb-2">UNSUBSCRIBED</h2>
            <p className="text-sm text-[#a3a3a3] mb-4">{"You've been unsubscribed from all Dynasty Trade Maker emails."}</p>
            <button onClick={resubscribe} disabled={saving} className="text-sm text-[#ccff00] hover:underline">{saving ? "Saving..." : "Changed your mind? Re-subscribe"}</button>
          </div>
        ) : (
          <div className="border border-[#262626] bg-[#171717] p-6 rounded-sm">
            <p className="text-xs text-[#666] mb-4">{"Choose which emails you'd like to receive:"}</p>
            <Toggle checked={prefs.weekly_buy_sell} onChange={v => save({ ...prefs, weekly_buy_sell: v })} label="Weekly Buy Low / Sell High" description="Monday — value movers with analysis on who to buy and sell" />
            <Toggle checked={prefs.weekly_rookie} onChange={v => save({ ...prefs, weekly_rookie: v })} label="Weekly Rookie Watch" description="Thursday — rookie risers and fallers with camp reports and draft capital context" />
            <Toggle checked={prefs.weekly_trade} onChange={v => save({ ...prefs, weekly_trade: v })} label="Trade of the Week" description="Tuesday — the most interesting real trades, graded and analyzed" />
            
            {saved && <p className="text-xs text-[#4ade80] text-center mt-4 flex items-center justify-center gap-1"><Check size={12} weight="bold" /> Preferences saved</p>}
            
            <div className="mt-6 pt-4 border-t border-[#262626] text-center">
              <button onClick={unsubscribeAll} disabled={saving} className="text-xs text-[#888] hover:text-[#ff4d4d] transition-colors">{saving ? "Saving..." : "Unsubscribe from all emails"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
